const FieldGroup = require('./FieldGroup');
const FieldInstance = require('./FieldInstance');
const FieldLevel = require('@/lib/CustomFields/FieldLevel');
const { getFieldValuesFromCart, setValuesOfFieldGroup } = require('@/lib/CustomFields/fieldFunctions');

class FieldInstanceFactory {
    /**
     * @param {FieldRepository} fieldRepository
     */
    constructor(
        fieldRepository
    ) {
        this.fieldRepository = fieldRepository;
    }

    /**
     * @param {Cart} cart
     *
     * @return {Promise}
     */
    async createFieldGroupsOnCart(cart) {
        // Load all of the active fields from IndexedDB once.
        // NOTE: We only show fields if their status is Active. This is inconsistent with some other entities, such
        // as sessions and items, because those are shown if they are either Active or Inactive. The Inactive status
        // is generally used to hide things from the customer facing site while still allowing them to
        // be used internally.
        // TODO: Make this consistent across all entities - include Inactive fields here.

        // const loadStart = new Date().getTime();
        const allFields = await this.fieldRepository.findAllActive();
        // console.debug(`Loaded ${allFields.length} fields in ` + (new Date().getTime() - loadStart) + 'ms');

        // Grab any existing field values from the cart as we will retain these on the new instances.
        // We do his here instead of passing them in as a prop so that this happens *after* loading fields
        // from IndexedDB has happened, as that can take a few ms.
        const originalFieldValues = getFieldValuesFromCart(cart);

        const orderLevelFields = this.fieldRepository.filterFieldsForLines(
            allFields,
            FieldLevel.ORDER, // Get all order level fields.
            cart.itemList, // That either always apply or are applicable to the items in the cart.
            false // That are not 'askEarly' fields.
        );

        // Add the order level fields to a single FieldGroup on the cart.
        const orderLevelFieldGroup = this.createFieldGroupForOrderWithFields(orderLevelFields);
        // Re-set any existing values on this field group.
        setValuesOfFieldGroup(originalFieldValues, orderLevelFieldGroup);
        // Set the order level field group on the cart.
        cart.fields = orderLevelFieldGroup;

        // Add the item level fields to a FieldGroup on each ItemInstance in the cart.
        // eslint-disable-next-line unused-imports/no-unused-vars
        for (let line of cart.itemList) {
            let lineFields = this.fieldRepository.filterFieldsForLines(
                allFields,
                FieldLevel.ITEM, // Get all item level fields
                [line]
                // Note ask early fields are included.
            );

            line.itemInstances.forEach((itemInstance) => {
                // Create the field group for this item instance.
                const instanceFieldGroup = this.createFieldGroupForInstanceWithFields(
                    itemInstance,
                    line,
                    lineFields
                );

                // Re-set any existing values on this field group.
                setValuesOfFieldGroup(originalFieldValues, instanceFieldGroup);

                // Set the field group, with now contains any previous values, back on the cart.
                itemInstance.fields = instanceFieldGroup;
            });
        }
    }

    /**
     * @private
     *
     * Creates a single FieldGroup set up as an order level group containing the given Fields.
     *
     * @param {Field[]} fields
     *
     * @returns {FieldGroup}
     */
    createFieldGroupForOrderWithFields(fields) {
        let fieldGroup = new FieldGroup({
            level: FieldLevel.ORDER,
        });

        fields.forEach(
            /**
             * @param {Field} field
             */
            (field) => {
                let fieldInstance = new FieldInstance({ field });
                fieldGroup.add(fieldInstance);
            }
        );

        return fieldGroup;
    }

    /**
     * @private
     *
     * Creates a single FieldGroup set up for the given ItemInstance and line and containing the given Fields.
     *
     * @param {ItemInstance} itemInstance
     * @param {CartLine} line
     * @param {Field[]} fields The list of fields is passed in here to avoid repetitive field loading
     *                         because they will be the same for every instance on the line.
     *
     * @returns {FieldGroup}
     */
    createFieldGroupForInstanceWithFields(itemInstance, line, fields) {
        // Create a new Field Group.
        let fieldGroup = new FieldGroup({
            level: FieldLevel.ITEM,
            title: `${line.getName()} (${itemInstance.instance})`,
            titleWithCount: line.getNameWithQty(),
            subtitle: line.getSessionFullName(),
            lineNumber: line.lineNumber,
            itemInstance: itemInstance.instanceForFieldData,
        });

        fields.forEach(
            /**
             * @param {Field} field
             */
            (field) => {
                let fieldInstance = new FieldInstance({
                    field,
                    lineNumber: line.lineNumber,
                    itemInstance: itemInstance.instanceForFieldData,
                });
                fieldGroup.add(fieldInstance);
            }
        );

        return fieldGroup;
    }
}

module.exports = FieldInstanceFactory;
